// frontend/src/components/UpgradeModal.jsx
import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

/**
 * Plan comparison data — mirrors the backend seed (app/db/seed_plans.py).
 * Kept here (rather than fetched) so the modal renders instantly when a 402
 * fires; the source of truth for enforcement remains the backend.
 */
const PLANS = [
  { name: "Free", codes: "5", scans: "500", members: "1", api: false },
  { name: "Starter", codes: "50", scans: "5,000", members: "3", api: false },
  { name: "Pro", codes: "500", scans: "50,000", members: "10", api: true },
  { name: "Business", codes: "5,000", scans: "500,000", members: "50", api: true },
];

const ROWS = [
  { label: "QR Codes / mo", key: "codes" },
  { label: "Scans / mo", key: "scans" },
  { label: "Team Members", key: "members" },
  { label: "API Access", key: "api" },
];

function cell(plan, key) {
  if (key === "api") return plan.api ? "✓" : "—";
  return plan[key];
}

/**
 * UpgradeModal — shown when the API returns 402 (plan limit exceeded).
 *
 * Displays which limit was hit and a Free/Starter/Pro/Business comparison
 * table, with an Upgrade button that routes to /billing.
 *
 * @param {object}   props
 * @param {string}   props.feature  Human-readable feature name, e.g. "QR Codes".
 * @param {number}   props.current  Current usage at the time of the block.
 * @param {number}   props.limit    The plan's limit for this feature.
 * @param {Function} props.onClose  Called on backdrop click, Close, or Upgrade.
 */
function UpgradeModal({ feature, current, limit, onClose }) {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onClose();
    navigate("/billing");
  };

  // current/limit are 0/0 for boolean entitlements (e.g. API access); only show
  // the "(current/limit)" counts when there is a meaningful numeric limit.
  const showCounts = Number(limit) > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="upgrade-modal-title"
          className="text-lg font-semibold text-gray-900 mb-1"
        >
          You&rsquo;ve reached your {feature} limit
          {showCounts ? ` (${current}/${limit})` : ""}
        </h2>
        <p className="text-sm text-gray-600 mb-5">
          Upgrade your plan to unlock more {feature.toLowerCase()} and additional
          features.
        </p>

        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-3 font-medium">Feature</th>
                {PLANS.map((p) => (
                  <th key={p.name} className="py-2 px-3 font-medium text-center">
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.key} className="border-t border-gray-100">
                  <td className="py-2 pr-3 text-gray-700">{row.label}</td>
                  {PLANS.map((p) => (
                    <td
                      key={p.name}
                      className="py-2 px-3 text-center text-gray-900"
                    >
                      {cell(p, row.key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Maybe later
          </button>
          <button
            onClick={handleUpgrade}
            className="px-4 py-2 text-sm rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Upgrade
          </button>
        </div>
      </div>
    </div>
  );
}

UpgradeModal.propTypes = {
  feature: PropTypes.string.isRequired,
  current: PropTypes.number,
  limit: PropTypes.number,
  onClose: PropTypes.func.isRequired,
};

UpgradeModal.defaultProps = {
  current: 0,
  limit: 0,
};

export default UpgradeModal;
